import { mystParse } from "myst-parser";

import type { BuiltSchema } from "@panproto/core";
import yaml from "js-yaml";
import documentLexicon from "./lexicons/pulled/document.json";
import defsLexicon from "./lexicons/pulled/defs.json";
import facetLexicon from "./lexicons/pulled/facet.json";
import type { MystBlock, MystDirective, MystDocument, MystInline, MystTableCell } from "./types/myst-document";
import type { OxaBlock, OxaDocument, OxaInline } from "./types/oxa-document";
import type { OxaRecord, OxaRecordBlock, OxaRecordBlockquote, OxaRecordCode, OxaRecordDirective, OxaRecordFootnoteDefinition, OxaRecordHeading, OxaRecordImage, OxaRecordList, OxaRecordListItem, OxaRecordMath, OxaRecordParagraph, OxaRecordTable, OxaRecordTableCell } from "./types/oxa-record";

// mdast node types from myst-parser — untyped upstream, so we define minimal interfaces
// for the properties the lens actually accesses.
interface MdastNode { type: string; children?: MdastNode[]; value?: string; [key: string]: unknown }
interface MdastRoot { type: "root"; children: MdastNode[] }

type PanprotoInstance = import("@panproto/core").Panproto;

interface FacetFeature {
  $type: string;
  [key: string]: unknown;
}

interface Facet {
  index: { byteStart: number; byteEnd: number };
  features: FacetFeature[];
}

interface RichText {
  text: string;
  facets: Facet[];
}

const encoder = new TextEncoder();

const facetTypeToInline: Record<string, string> = {
  "pub.oxa.richtext.facet#strong": "Strong",
  "pub.oxa.richtext.facet#emphasis": "Emphasis",
  "pub.oxa.richtext.facet#superscript": "Superscript",
  "pub.oxa.richtext.facet#subscript": "Subscript",
  "pub.oxa.richtext.facet#inlineCode": "InlineCode",
  "pub.oxa.richtext.facet#link": "Link",
  "pub.oxa.richtext.facet#footnoteReference": "FootnoteReference",
  "pub.leaflet.richtext.facet#bold": "Strong",
  "pub.leaflet.richtext.facet#italic": "Emphasis",
  "pub.leaflet.richtext.facet#code": "InlineCode",
};

const inlineFacetTypes: Record<string, string> = {
  Strong: "pub.oxa.richtext.facet#strong",
  Emphasis: "pub.oxa.richtext.facet#emphasis",
  Superscript: "pub.oxa.richtext.facet#superscript",
  Subscript: "pub.oxa.richtext.facet#subscript",
  InlineCode: "pub.oxa.richtext.facet#inlineCode",
  Link: "pub.oxa.richtext.facet#link",
  FootnoteReference: "pub.oxa.richtext.facet#footnoteReference",
};

type MystBlockNode = MystBlock;
type MystInlineNode = MystInline;
type OxaBlockNode = OxaBlock;
type OxaInlineNode = OxaInline;

type JsonTree = Record<string, unknown>;

const ADMONITION_NAMES = new Set([
  "note",
  "warning",
  "danger",
  "error",
  "tip",
  "important",
  "caution",
  "attention",
  "hint",
  "seealso",
  "admonition",
]);

function normalizeDirectiveAttributes(attributes: Record<string, string | null | undefined> | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(attributes ?? {})) {
    if (value === undefined || value === null) continue;
    result[key] = String(value);
  }
  return result;
}

function directiveAttributesToMarkdown(attributes: Record<string, string> | undefined): string {
  const entries = Object.entries(attributes ?? {});
  if (entries.length === 0) return "";
  return "{" + entries.map(([key, value]) => key + "=" + JSON.stringify(value)).join(" ") + "}";
}

function renderInlineNodes(nodes: OxaInlineNode[] | MystInlineNode[] | undefined, renderer: (node: OxaInlineNode | MystInlineNode) => string): string {
  return (nodes ?? []).map(renderer).join("");
}

function escapeTableCell(text: string): string {
  return text.replace(/\|/g, "\\|").replace(/\n/g, " ");
}

interface TableRowLike { children?: unknown[]; cells?: unknown[] }
function renderMarkdownTable(
  rows: TableRowLike[] | undefined,
  headerRows: number | undefined,
  renderCell: (cell: unknown) => string,
): string {
  const tableRows = rows ?? [];
  if (tableRows.length === 0) return "";

  const headerCount = Math.max(1, headerRows ?? 1);
  const headerRow = tableRows[0];
  const headerCells = ((headerRow.children ?? headerRow.cells) ?? []).map((cell: unknown) => escapeTableCell(renderCell(cell)));
  const bodyRows = tableRows.slice(headerCount);
  const lines = [
    "| " + headerCells.join(" | ") + " |",
    "| " + headerCells.map(() => "---").join(" | ") + " |",
  ];

  for (const row of bodyRows) {
    const cells = ((row.children ?? row.cells) ?? []).map((cell: unknown) => escapeTableCell(renderCell(cell)));
    lines.push("| " + cells.join(" | ") + " |");
  }

  return lines.join("\n") + "\n\n";
}

function renderDirectiveBody(block: OxaRecordDirective): string {
  const options = normalizeDirectiveAttributes(block.options ?? (block as unknown as { attributes?: Record<string, string | null | undefined> }).attributes);
  const argument = typeof block.argument === "string" ? block.argument : "";
  const body = typeof block.body === "string" ? block.body : "";
  const children = Array.isArray(block.children) ? block.children : [];

  let result = "```{" + (block.name || "directive") + "}";
  if (argument) result += " " + argument;
  result += "\n";

  for (const [key, value] of Object.entries(options)) {
    result += ":" + key + ": " + value + "\n";
  }

  // Render from body (raw) if present, otherwise from children (parsed)
  if (body) {
    result += "\n" + body + "\n";
  } else if (children.length > 0) {
    const rendered = children.map((child: OxaRecordBlock) => {
      const childType = child.$type;
      // Strip $$ delimiters from math blocks inside directives
      if (childType === "pub.oxa.blocks.defs#math") {
        return ("value" in child ? child.value : "") + "\n\n";
      }
      // Render tables as list-table format inside directives
      if (childType === "pub.oxa.blocks.defs#table") {
        return renderTableAsListTable(child as OxaRecordTable);
      }
      return oxaBlockToMarkdown(child);
    }).join("");
    result += "\n" + rendered;
  }
  return result + "```\n\n";
}

function renderTableAsListTable(table: OxaRecordTable): string {
  const rows = table.children || [];
  const headerRows = typeof table.headerRows === "number" ? table.headerRows : 0;
  const lines: string[] = [];

  for (let i = 0; i < rows.length; i++) {
    const cells = rows[i].children || [];
    const prefix = "* - ";
    const cellPrefix = "  - ";

    for (let j = 0; j < cells.length; j++) {
      const cellText = cells[j].text || "";
      if (j === 0) {
        lines.push(prefix + cellText);
      } else {
        lines.push(cellPrefix + cellText);
      }
    }
  }

  return lines.join("\n") + "\n\n";
}

function byteLength(text: string): number {
  return encoder.encode(text).byteLength;
}

function textBetweenBytes(text: string, byteStart: number, byteEnd: number): string {
  const bytes = encoder.encode(text);
  return new TextDecoder().decode(bytes.slice(byteStart, byteEnd));
}

function extractInlineText(node: OxaInlineNode): string {
  switch (node.type) {
    case "Text":
    case "InlineCode":
    case "InlineMath":
      return node.value;
    case "Link":
      return node.children ? node.children.map(extractInlineText).join("") : "";
    case "Opaque":
      return typeof node.payload_json === "string" ? node.payload_json : "";
    default:
      return "";
  }
}

function unflattenInlines(richText: Partial<RichText> & { text?: string }): OxaInlineNode[] {
  const sourceText = richText.text ?? "";
  const facets = richText.facets ?? [];
  if (facets.length === 0) {
    return sourceText.length > 0 ? [{ type: "Text", value: sourceText }] : [];
  }

  // Separate zero-length facets from normal ones
  const zeroLengthNodes: Array<{ bytePos: number; node: OxaInlineNode }> = [];
  const normalFacets: Facet[] = [];

  for (const facet of facets) {
    if (facet.index.byteStart === facet.index.byteEnd) {
      // Zero-length facet: convert directly to an inline node
      for (const feature of facet.features) {
        const inlineType = facetTypeToInline[feature.$type];
        if (!inlineType) continue;
        if (inlineType === "Link") {
          zeroLengthNodes.push({ bytePos: facet.index.byteStart, node: { type: "Link", target: typeof feature.uri === "string" ? feature.uri : "" } });
        } else if (inlineType === "InlineCode") {
          const lang = typeof feature.language === "string" ? feature.language : undefined;
          if (lang === "latex") {
            zeroLengthNodes.push({ bytePos: facet.index.byteStart, node: { type: "InlineMath", value: "" } });
          } else {
            const n: OxaInlineNode = { type: "InlineCode", value: "" };
            if (lang !== undefined) n.language = lang;
            zeroLengthNodes.push({ bytePos: facet.index.byteStart, node: n });
          }
        } else {
          zeroLengthNodes.push({ bytePos: facet.index.byteStart, node: { type: inlineType as OxaInline["type"], children: [] } as OxaInlineNode });
        }
      }
    } else {
      normalFacets.push(facet);
    }
  }

  // Sort zero-length nodes by byte position
  zeroLengthNodes.sort((a, b) => a.bytePos - b.bytePos);

  // Build boundary events from normal facets
  const boundaries = new Map<number, { starts: string[]; ends: string[] }>();
  const facetLanguages = new Map<number, string>();
  const facetLinkTargets = new Map<number, string>();
  const facetFootnoteLabels = new Map<number, string>();

  for (const facet of normalFacets) {
    const { byteStart, byteEnd } = facet.index;
    const seen = new Set<string>();
    for (const feature of facet.features) {
      const inlineType = facetTypeToInline[feature.$type];
      if (!inlineType || seen.has(inlineType)) continue;
      seen.add(inlineType);

      if (!boundaries.has(byteStart)) boundaries.set(byteStart, { starts: [], ends: [] });
      boundaries.get(byteStart)!.starts.push(inlineType);
      if (!boundaries.has(byteEnd)) boundaries.set(byteEnd, { starts: [], ends: [] });
      boundaries.get(byteEnd)!.ends.push(inlineType);

      if (inlineType === "InlineCode" && typeof feature.language === "string") {
        facetLanguages.set(byteStart, feature.language);
      }
      if (inlineType === "Link" && typeof feature.uri === "string") {
        facetLinkTargets.set(byteStart, feature.uri);
      }
      if (inlineType === "FootnoteReference" && typeof feature.label === "string") {
        facetFootnoteLabels.set(byteStart, feature.label);
      }
    }
  }

  // Walk boundaries, interleaving zero-length nodes at their byte positions
  const segments: Array<{ byteStart: number; byteEnd: number; text: string; types: string[]; language?: string; linkTarget?: string; footnoteLabel?: string }> = [];
  const sorted = [...boundaries.entries()].sort((a, b) => a[0] - b[0]);
  const active: string[] = [];
  let prev = 0;
  let zlIdx = 0;

  for (const [bytePos, events] of sorted) {
    if (bytePos > prev) {
      // Emit zero-length nodes that fall in this range
      while (zlIdx < zeroLengthNodes.length && zeroLengthNodes[zlIdx].bytePos < bytePos) {
        const zl = zeroLengthNodes[zlIdx];
        // Emit text from prev to zl.bytePos
        if (zl.bytePos > prev) {
          const segText = textBetweenBytes(sourceText, prev, zl.bytePos);
          if (segText.length > 0) {
            segments.push({ byteStart: prev, byteEnd: zl.bytePos, text: segText, types: [...active] });
          }
        }
        // Emit the zero-length node as a special segment
        segments.push({ byteStart: zl.bytePos, byteEnd: zl.bytePos, text: "\0", types: [], language: undefined, linkTarget: undefined, footnoteLabel: undefined });
        // Store the node for later reconstruction
        prev = zl.bytePos;
        zlIdx++;
      }

      // Emit text from prev to bytePos
      const segmentText = textBetweenBytes(sourceText, prev, bytePos);
      if (segmentText.length > 0) {
        const segment: typeof segments[0] = {
          byteStart: prev,
          byteEnd: bytePos,
          text: segmentText,
          types: [...active],
        };
        if (facetLanguages.has(prev)) segment.language = facetLanguages.get(prev);
        if (facetLinkTargets.has(prev)) segment.linkTarget = facetLinkTargets.get(prev);
        if (facetFootnoteLabels.has(prev)) segment.footnoteLabel = facetFootnoteLabels.get(prev);
        segments.push(segment);
      }
    }
    for (const t of events.ends) {
      const idx = active.lastIndexOf(t);
      if (idx !== -1) active.splice(idx, 1);
    }
    for (const t of events.starts) active.push(t);
    prev = bytePos;
  }

  // Emit remaining zero-length nodes and text
  while (zlIdx < zeroLengthNodes.length) {
    const zl = zeroLengthNodes[zlIdx];
    if (zl.bytePos > prev) {
      const segText = textBetweenBytes(sourceText, prev, zl.bytePos);
      if (segText.length > 0) segments.push({ byteStart: prev, byteEnd: zl.bytePos, text: segText, types: [...active] });
    }
    segments.push({ byteStart: zl.bytePos, byteEnd: zl.bytePos, text: "\0", types: [], language: undefined, linkTarget: undefined });
    prev = zl.bytePos;
    zlIdx++;
  }

  if (prev < byteLength(sourceText)) {
    const textSlice = textBetweenBytes(sourceText, prev, byteLength(sourceText));
    if (textSlice.length > 0) segments.push({ byteStart: prev, byteEnd: byteLength(sourceText), text: textSlice, types: [] });
  }

  // Build the inline tree, replacing \0 markers with the zero-length nodes
  const inlineTree = buildInlineTree(segments);
  const result: OxaInlineNode[] = [];
  let zlResultIdx = 0;
  for (const node of inlineTree) {
    if (node.type === "Text" && node.value === "\0") {
      if (zlResultIdx < zeroLengthNodes.length) {
        result.push(zeroLengthNodes[zlResultIdx].node);
        zlResultIdx++;
      }
    } else {
      result.push(node);
    }
  }
  return result;
}

function buildInlineTree(segments: Array<{ byteStart: number; byteEnd: number; text: string; types: string[]; language?: string; linkTarget?: string; footnoteLabel?: string }>): OxaInlineNode[] {
  const result: OxaInlineNode[] = [];
  let i = 0;

  while (i < segments.length) {
    const seg = segments[i];
    if (seg.types.length === 0) {
      result.push({ type: "Text", value: seg.text });
      i++;
      continue;
    }

    const outer = seg.types[0];
    const inner: typeof segments = [];
    let j = i;
    while (j < segments.length && segments[j].types.length > 0 && segments[j].types[0] === outer) {
      inner.push({ ...segments[j], types: segments[j].types.slice(1) });
      j++;
    }
    const children = buildInlineTree(inner);

    if (outer === "InlineCode") {
      const value = children.map(extractInlineText).join("");
      const lang = inner.find((s) => s.language !== undefined)?.language;
      if (lang === "latex") {
        result.push({ type: "InlineMath", value });
      } else {
        const node: OxaInlineNode = { type: "InlineCode", value };
        if (lang !== undefined) node.language = lang;
        result.push(node);
      }
    } else if (outer === "Link") {
      const link: OxaInlineNode = { type: "Link", target: inner.find((s) => s.linkTarget !== undefined)?.linkTarget ?? "" };
      if (children.length > 0) link.children = children;
      result.push(link);
    } else if (outer === "FootnoteReference") {
      const label = inner.find((s) => s.footnoteLabel !== undefined)?.footnoteLabel ?? "";
      result.push({ type: "FootnoteReference", label });
    } else {
      result.push({ type: outer as OxaInline["type"], children } as OxaInlineNode);
    }

    i = j;
  }

  return result;
}

function flattenInlines(inlines: OxaInlineNode[]): { text: string; facets: Facet[] } {
  const facets: Facet[] = [];
  let text = "";

  function walk(nodes: OxaInlineNode[]): void {
    for (const node of nodes) {
      if (node.type === "Text") {
        text += node.value;
      } else if (node.type === "InlineMath") {
        const byteStart = byteLength(text);
        text += node.value;
        const byteEnd = byteLength(text);
        facets.push({ index: { byteStart, byteEnd }, features: [{ $type: "pub.oxa.richtext.facet#inlineCode", language: "latex" }] });
      } else if (node.type === "InlineCode") {
        const byteStart = byteLength(text);
        text += node.value;
        const byteEnd = byteLength(text);
        facets.push({ index: { byteStart, byteEnd }, features: [{ $type: "pub.oxa.richtext.facet#inlineCode" }] });
      } else if (node.type === "Link") {
        const byteStart = byteLength(text);
        if (node.children) walk(node.children);
        const byteEnd = byteLength(text);
        facets.push({ index: { byteStart, byteEnd }, features: [{ $type: "pub.oxa.richtext.facet#link", uri: node.target }] });
      } else if (node.type === "FootnoteReference") {
        const byteStart = byteLength(text);
        const label = node.label ?? "";
        text += `[^${label}]`;
        const byteEnd = byteLength(text);
        facets.push({ index: { byteStart, byteEnd }, features: [{ $type: "pub.oxa.richtext.facet#footnoteReference", label }] });
      } else if ("children" in node) {
        const facetType = inlineFacetTypes[node.type];
        if (facetType) {
          const byteStart = byteLength(text);
          walk(node.children);
          const byteEnd = byteLength(text);
          facets.push({ index: { byteStart, byteEnd }, features: [{ $type: facetType }] });
        } else {
          text += extractInlineText(node as OxaInlineNode);
        }
      }
    }
  }

  walk(inlines);
  return { text, facets };
}

function renderMdastTextDirective(node: MdastNode): string {
  const label = renderInlineNodes(mdastInlineToOxa(node.children || []), oxaInlineToMarkdown);
  const attributes = directiveAttributesToMarkdown(normalizeDirectiveAttributes(node.attributes as Record<string, string | null | undefined> | undefined));
  return ":" + String(node.name) + "[" + label + "]" + attributes;
}

function mdastInlineToOxa(nodes: MdastNode[]): OxaInlineNode[] {
  const result: OxaInlineNode[] = [];
  for (const node of nodes) {
    switch (node.type) {
      case "text":
        result.push({ type: "Text", value: node.value });
        break;
      case "strong":
        result.push({ type: "Strong", children: mdastInlineToOxa(node.children || []) });
        break;
      case "emphasis":
        result.push({ type: "Emphasis", children: mdastInlineToOxa(node.children || []) });
        break;
      case "inlineCode":
        result.push({ type: "InlineCode", value: node.value });
        break;
      case "inlineMath":
        result.push({ type: "InlineMath", value: node.value });
        break;
      case "link":
        result.push({ type: "Link", target: node.url as string, children: mdastInlineToOxa(node.children || []) });
        break;
      case "footnoteReference":
        result.push({ type: "FootnoteReference", label: String(node.label ?? node.identifier ?? "") });
        break;
      case "textDirective":
        result.push({
          type: "Opaque",
          source_type: "mdast:textDirective",
          payload_json: renderMdastTextDirective(node),
        });
        break;
      default:
        if (node.children) result.push(...mdastInlineToOxa(node.children));
        else if (node.value) result.push({ type: "Text", value: node.value });
        break;
    }
  }
  return result;
}

function mdastToAtprotoBlocks(nodes: MdastNode[]): { blocks: OxaRecordBlock[]; metadata: Record<string, unknown> } {
  const blocks: OxaRecordBlock[] = [];
  const metadata: Record<string, unknown> = {};

  for (const node of nodes) {
    switch (node.type) {
      case "yaml":
        if (typeof node.value === "string") {
          const parsed = yaml.load(node.value);
          if (typeof parsed === "object" && parsed !== null) {
            for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
              if (!(key in metadata)) metadata[key] = value;
            }
          }
        }
        break;
      case "code":
        // myst-parser represents frontmatter as a code block with lang="yaml"
        if (node.lang === "yaml" && blocks.length === 0 && Object.keys(metadata).length === 0) {
          const parsed = yaml.load(node.value!);
          if (typeof parsed === "object" && parsed !== null) {
            for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
              if (!(key in metadata)) metadata[key] = value;
            }
          }
          break;
        }
        {
          const block: OxaRecordBlock = { $type: "pub.oxa.blocks.defs#code", value: node.value! };
          if (node.lang) (block as OxaRecordCode).language = node.lang as string;
          blocks.push(block);
        }
        break;
      case "paragraph": {
        const { text, facets } = flattenInlines(mdastInlineToOxa(node.children || []));
        blocks.push({ $type: "pub.oxa.blocks.defs#paragraph", text, facets });
        break;
      }
      case "heading": {
        const { text, facets } = flattenInlines(mdastInlineToOxa(node.children || []));
        blocks.push({ $type: "pub.oxa.blocks.defs#heading", level: node.depth as number, text, facets });
        break;
      }
      case "thematicBreak":
        blocks.push({ $type: "pub.oxa.blocks.defs#thematicBreak" });
        break;
      case "blockquote":
        blocks.push({ $type: "pub.oxa.blocks.defs#blockquote", children: mdastToAtprotoBlocks(node.children || []).blocks });
        break;
      case "list":
        blocks.push({
          $type: "pub.oxa.blocks.defs#list",
          ordered: !!node.ordered,
          children: (node.children || []).map((li: MdastNode) => ({
            $type: "pub.oxa.blocks.defs#listItem",
            children: mdastToAtprotoBlocks(li.children || []).blocks,
          })),
        });
        break;
      case "math":
        blocks.push({ $type: "pub.oxa.blocks.defs#math", value: node.value });
        break;
      case "image":
        blocks.push({ $type: "pub.oxa.blocks.defs#image", src: node.url as string, ...(node.alt ? { alt: node.alt as string } : {}) });
        break;
      case "table": {
        const rows = (node.children || []).map((row: MdastNode) => ({
          $type: "pub.oxa.blocks.defs#tableRow" as const,
          children: (row.children || []).map((cell: MdastNode) => {
            const { text, facets } = flattenInlines(mdastInlineToOxa(cell.children || []));
            return {
              $type: "pub.oxa.blocks.defs#tableCell" as const,
              text,
              facets,
            };
          }),
        }));
        blocks.push({ $type: "pub.oxa.blocks.defs#table", headerRows: node.headerRows as number | undefined, children: rows });
        break;
      }
      case "footnoteDefinition": {
        const childBlocks = mdastToAtprotoBlocks(node.children || []).blocks;
        blocks.push({
          $type: "pub.oxa.blocks.defs#footnoteDefinition",
          label: String(node.label ?? node.identifier ?? ""),
          identifier: node.identifier ? String(node.identifier) : undefined,
          children: childBlocks,
        });
        break;
      }
      case "containerDirective": {
        const children = node.children || [];
        const hasLabelParagraph = children.length > 0 && children[0].type === "paragraph" && !!(children[0].data as Record<string, unknown> | undefined)?.directiveLabel;
        const labelChildren = hasLabelParagraph ? children[0].children || [] : [];
        const bodyChildren = hasLabelParagraph ? children.slice(1) : children;
        const directive: OxaRecordDirective = {
          $type: "pub.oxa.blocks.defs#directive",
          name: node.name as string,
          options: normalizeDirectiveAttributes(node.attributes as Record<string, string | null | undefined> | undefined),
        };
        const argument = renderInlineNodes(mdastInlineToOxa(labelChildren), oxaInlineToMarkdown).trim();
        if (argument.length > 0) directive.argument = argument;
        const childBlocks = mdastToAtprotoBlocks(bodyChildren).blocks;
        if (childBlocks.length > 0) directive.children = childBlocks;
        blocks.push(directive);
        break;
      }
      case "leafDirective": {
        const directive: OxaRecordDirective = {
          $type: "pub.oxa.blocks.defs#directive",
          name: node.name as string,
          options: normalizeDirectiveAttributes(node.attributes as Record<string, string | null | undefined> | undefined),
        };
        const argument = renderInlineNodes(mdastInlineToOxa(node.children || []), oxaInlineToMarkdown).trim();
        if (argument.length > 0) directive.argument = argument;
        blocks.push(directive);
        break;
      }
      case "mystDirective": {
        const directive: OxaRecordDirective = {
          $type: "pub.oxa.blocks.defs#directive",
          name: node.name as string,
          options: normalizeDirectiveAttributes(node.options as Record<string, string | null | undefined> | undefined),
        };
        if (typeof node.args === "string" && (node.args as string).length > 0) directive.argument = node.args as string;
        // Process children — unwrap containers and admonitions, skip title/caption nodes
        const childNodes: MdastNode[] = [];
        for (const child of (node.children || [])) {
          if (child.type === "container" || child.type === "admonition") {
            // Unwrap — skip title/caption nodes (they duplicate the directive argument)
            for (const inner of (child.children || [])) {
              if (inner.type === "admonitionTitle" || inner.type === "caption") continue;
              // For figure directives, skip the image child — it's derived from the argument
              if (node.name === "figure" && inner.type === "image") continue;
              childNodes.push(inner);
            }
          } else if (child.type === "math") {
            // Math directives have a direct math child — use it as-is
            childNodes.push(child);
          } else {
            childNodes.push(child);
          }
        }
        const childResult = mdastToAtprotoBlocks(childNodes);
        if (childResult.blocks.length > 0) directive.children = childResult.blocks;
        for (const [key, value] of Object.entries(childResult.metadata)) {
          if (!metadata[key]) metadata[key] = value;
        }
        blocks.push(directive);
        break;
      }
      default:
        if (node.children) {
          const childResult = mdastToAtprotoBlocks(node.children);
          if (childResult.blocks.length > 0) {
            blocks.push(...childResult.blocks);
            for (const [key, value] of Object.entries(childResult.metadata)) {
              if (!metadata[key]) metadata[key] = value;
            }
          } else {
            const { text, facets } = flattenInlines(mdastInlineToOxa(node.children));
            blocks.push({ $type: "pub.oxa.blocks.defs#paragraph", text, facets });
          }
        }
        break;
    }
  }

  return { blocks, metadata };
}

function mystInlineToMarkdown(inline: MystInlineNode): string {
  switch (inline.type) {
    case "text":
      return inline.value;
    case "strong":
      return "**" + renderInlineNodes(inline.children, mystInlineToMarkdown) + "**";
    case "emphasis":
      return "*" + renderInlineNodes(inline.children, mystInlineToMarkdown) + "*";
    case "inline_code":
      return "`" + inline.value + "`";
    case "inline_math":
      return "$" + inline.value + "$";
    case "link": {
      const url = inline.target;
      return "[" + (inline.children ? renderInlineNodes(inline.children, mystInlineToMarkdown) : url) + "](" + url + ")";
    }
    case "role":
      return inline.name === "math" ? "$" + inline.content + "$" : "{" + inline.name + "}" + inline.content;
  }
  return "";
}

function escapeStandaloneDirectiveClosers(markdown: string): string {
  return markdown.replace(/(^|\n)([ \t]*):::(?=\n|$)/g, "$1$2\\:::");
}

function renderMystDirective(block: MystDirective): string {
  const options = block.options ?? {};
  const hasBody = typeof block.body === "string" && block.body.length > 0;
  const hasChildren = Array.isArray(block.children) && block.children.length > 0;

  let result = "```{" + block.name + "}";
  if (block.argument) result += " " + block.argument;
  result += "\n";
  for (const [key, value] of Object.entries(options)) {
    result += ":" + key + ": " + value + "\n";
  }
  if (hasBody) result += "\n" + block.body + "\n";
  if (hasChildren) result += "\n" + block.children!.map(mystBlockToMarkdown).join("") + "\n";
  return result + "```\n\n";
}

function mystBlockToMarkdown(block: MystBlockNode): string {
  switch (block.type) {
    case "heading":
      return "#".repeat(block.level) + " " + renderInlineNodes(block.children, mystInlineToMarkdown) + "\n\n";
    case "paragraph":
      return renderInlineNodes(block.children, mystInlineToMarkdown) + "\n\n";
    case "code_block":
      return "```" + (block.language || "") + "\n" + block.value + "\n```\n\n";
    case "thematic_break":
      return "---\n\n";
    case "blockquote":
      return block.children.map(mystBlockToMarkdown).join("").split("\n").map((line) => (line ? "> " + line : ">" )).join("\n") + "\n";
    case "image":
      return "![" + (block.alt || "") + "](" + block.src + ")\n\n";
    case "math_block":
      return "$$\n" + block.value + "\n$$\n\n";
    case "ordered_list":
      return block.children.map((item, index) => {
        const inner = escapeStandaloneDirectiveClosers(item.children.map(mystBlockToMarkdown).join("").replace(/\n+$/, ""));
        return String((block.startIndex ?? 1) + index) + ". " + inner.replace(/\n/g, "\n   ");
      }).join("\n") + "\n\n";
    case "unordered_list":
      return block.children.map((item) => {
        const inner = escapeStandaloneDirectiveClosers(item.children.map(mystBlockToMarkdown).join("").replace(/\n+$/, ""));
        return "- " + inner.replace(/\n/g, "\n  ");
      }).join("\n") + "\n\n";
    case "directive":
      return renderMystDirective(block);
    case "table":
      return renderMarkdownTable(block.children, block.headerRows, (cell) => renderInlineNodes((cell as MystTableCell).children, mystInlineToMarkdown));
  }
  return "";
}

function oxaInlineToMarkdown(inline: OxaInlineNode): string {
  switch (inline.type) {
    case "Text":
      return inline.value;
    case "Strong":
      return "**" + renderInlineNodes(inline.children, oxaInlineToMarkdown) + "**";
    case "Emphasis":
      return "*" + renderInlineNodes(inline.children, oxaInlineToMarkdown) + "*";
    case "InlineCode":
      return inline.language === "latex" ? "$" + inline.value + "$" : "`" + inline.value + "`";
    case "InlineMath":
      return "$" + inline.value + "$";
    case "Link":
      if (inline.children && inline.children.length > 0) {
        return "[" + renderInlineNodes(inline.children, oxaInlineToMarkdown) + "](" + inline.target + ")";
      }
      return "[](" + inline.target + ")";
    case "Superscript":
      return "^" + renderInlineNodes(inline.children, oxaInlineToMarkdown);
    case "Subscript":
      return "~" + renderInlineNodes(inline.children, oxaInlineToMarkdown);
    case "FootnoteReference":
      return `[^${inline.label}]`;
    case "Opaque":
      return inline.payload_json;
  }
  return "";
}

function renderDirectiveFromRecord(block: OxaRecordDirective): string {
  return renderDirectiveBody(block);
}

function oxaBlockToMarkdown(block: OxaRecordBlock): string {
  switch (block.$type) {
    case "pub.oxa.blocks.defs#paragraph": {
      const p = block as OxaRecordParagraph;
      return ((unflattenInlines(p).map(oxaInlineToMarkdown).join("")) || p.text || "") + "\n\n";
    }
    case "pub.oxa.blocks.defs#heading": {
      const h = block as OxaRecordHeading;
      return "#".repeat(h.level || 1) + " " + ((unflattenInlines(h).map(oxaInlineToMarkdown).join("")) || h.text || "") + "\n\n";
    }
    case "pub.oxa.blocks.defs#code":
      return "```" + (block as OxaRecordCode).language + "\n" + (block as OxaRecordCode).value + "\n```\n\n";
    case "pub.oxa.blocks.defs#thematicBreak":
      return "---\n\n";
    case "pub.oxa.blocks.defs#blockquote":
      return ((block as OxaRecordBlockquote).children || []).map(oxaBlockToMarkdown).join("").split("\n").map((line: string) => (line ? "> " + line : ">")).join("\n") + "\n";
    case "pub.oxa.blocks.defs#image": {
      const img = block as OxaRecordImage;
      return "![" + (img.alt || "") + "](" + img.src + ")\n\n";
    }
    case "pub.oxa.blocks.defs#math":
      return "$$\n" + (block as OxaRecordMath).value + "\n$$\n\n";
    case "pub.oxa.blocks.defs#list": {
      const list = block as OxaRecordList;
      const items = list.children || [];
      return items.map((item: OxaRecordListItem, index: number) => {
        const inner = escapeStandaloneDirectiveClosers((item.children || []).map(oxaBlockToMarkdown).join("").replace(/\n+$/, ""));
        const prefix = list.ordered ? String((list.startIndex ?? 1) + index) + ". " : "- ";
        return prefix + inner.replace(/\n/g, list.ordered ? "\n   " : "\n  ");
      }).join("\n") + "\n\n";
    }
    case "pub.oxa.blocks.defs#listItem":
      return ((block as OxaRecordListItem).children || []).map(oxaBlockToMarkdown).join("");
    case "pub.oxa.blocks.defs#table": {
      const tbl = block as OxaRecordTable;
      return renderMarkdownTable(tbl.children, tbl.headerRows, (cell) => {
        const c = cell as OxaRecordTableCell;
        const inlineText = renderInlineNodes(unflattenInlines(c), oxaInlineToMarkdown);
        return inlineText || c.text || "";
      });
    }
    case "pub.oxa.blocks.defs#directive":
      return renderDirectiveFromRecord(block as OxaRecordDirective);
    case "pub.oxa.blocks.defs#footnoteDefinition": {
      const fn = block as OxaRecordFootnoteDefinition;
      const label = fn.label ?? "";
      const children = (fn.children || []).map(oxaBlockToMarkdown).join("").replace(/\n+$/, "");
      return `[^${label}]: ${children.replace(/\n/g, "    \n")}\n\n`;
    }
    default:
      return "";
  }
}

export async function mystTextToOxaRecord(mystText: string, title?: string): Promise<OxaRecord> {
  const tree = mystParse(mystText) as MdastRoot;
  const { blocks, metadata } = mdastToAtprotoBlocks(tree.children);
  const docTitle = title ?? (typeof metadata.title === "string" ? metadata.title : "");
  const record: OxaRecord = {
    $type: "pub.oxa.document",
    title: { text: docTitle, facets: [] },
    children: blocks,
    createdAt: new Date().toISOString(),
  };

  if (Object.keys(metadata).length > 0) {
    record.metadata = metadata;
  }

  // Use core when it can validate the pulled lexicon; keep the handwritten record when it cannot.
  try {
    const validated = await validateOxaDocument(record);
    return validated as OxaRecord;
  } catch {
    return record;
  }
}

export function oxaRecordToMystText(record: OxaRecord): string {
  if (!record) return "";
  const children = Array.isArray(record.children) ? record.children : [];
  const metadata = record.metadata && typeof record.metadata === "object" ? record.metadata : {};
  const metadataKeys = Object.keys(metadata).filter(k => metadata[k] !== undefined && metadata[k] !== null);
  const parts: string[] = [];
  if (metadataKeys.length > 0) {
    const yamlStr = yaml.dump(metadata, { lineWidth: -1, noRefs: true });
    parts.push("---\n" + yamlStr + "---\n\n");
  }
  parts.push(children.map(oxaBlockToMarkdown).join(""));
  return parts.join("").trimEnd();
}

export interface LensResult<View, Complement> {
  view: View;
  complement: Complement;
}

export interface Lens<Source, View, Complement> {
  get(source: Source): Promise<LensResult<View, Complement>>;
  put(view: View, complement: Complement): Promise<Source>;
}

export interface DocumentComplement {
  metadata: Record<string, unknown>;
  data: Record<string, unknown>;
}

export type DocumentView = {
  type: "Document";
  tree_json: string;
  metadata?: Record<string, unknown>;
  data?: Record<string, unknown>;
};

export type ReverseDocumentView = {
  type: "document";
  tree_json: string;
  metadata?: Record<string, unknown>;
  data?: Record<string, unknown>;
};

export const mystDocumentSchema = Object.freeze({
  family: "document",
  format: "myst",
  version: 1,
} as const);

export const oxaDocumentSchema = Object.freeze({
  family: "document",
  format: "oxa",
  version: 1,
} as const);

export type DocumentSchema = typeof mystDocumentSchema | typeof oxaDocumentSchema;

function isDocumentSchema(schema: unknown): schema is DocumentSchema {
  return (
    typeof schema === "object" &&
    schema !== null &&
    (schema as { family?: string }).family === "document" &&
    (schema as { version?: number }).version === 1 &&
    ((schema as { format?: string }).format === "myst" || (schema as { format?: string }).format === "oxa")
  );
}

const coreDocumentLexicons = Object.freeze({
  myst: {
    lexicon: 1,
    id: "science.panproto.mystDocument",
    defs: {
      main: {
        type: "record",
        key: "tid",
        record: {
          type: "object",
          properties: {
            type: { type: "string" },
            tree_json: { type: "string" },
            metadata: { type: "unknown" },
            data: { type: "unknown" },
          },
          required: ["type", "tree_json", "metadata", "data"],
        },
      },
    },
  },
  oxa: {
    lexicon: 1,
    id: "science.panproto.oxaDocument",
    defs: {
      main: {
        type: "record",
        key: "tid",
        record: {
          type: "object",
          properties: {
            type: { type: "string" },
            tree_json: { type: "string" },
            metadata: { type: "unknown" },
            data: { type: "unknown" },
          },
          required: ["type", "tree_json", "metadata", "data"],
        },
      },
    },
  },
} as const);

let corePromise: Promise<PanprotoInstance> | undefined;
let coreSchemaPromise:
  | Promise<{ myst: BuiltSchema; oxa: BuiltSchema }>
  | undefined;

async function getPanproto(): Promise<PanprotoInstance> {
  corePromise ??= (async () => {
    const loader = new Function("return import('@panproto/core')") as () => Promise<typeof import("@panproto/core")>;
    const module = await loader();
    return module.Panproto.init();
  })();
  return corePromise;
}

async function getCoreDocumentSchemas(): Promise<{ myst: BuiltSchema; oxa: BuiltSchema }> {
  if (!coreSchemaPromise) {
    coreSchemaPromise = (async () => {
      const core = await getPanproto();
      return {
        myst: core.parseLexicon(coreDocumentLexicons.myst),
        oxa: core.parseLexicon(coreDocumentLexicons.oxa),
      };
    })();
  }
  return coreSchemaPromise;
}

let oxaValidationSchemaPromise: Promise<BuiltSchema> | undefined;

async function getOxaValidationSchema(): Promise<BuiltSchema> {
  if (!oxaValidationSchemaPromise) {
    oxaValidationSchemaPromise = (async () => {
      const core = await getPanproto();
      core.parseLexicon(facetLexicon);
      core.parseLexicon(defsLexicon);
      return core.parseLexicon(documentLexicon);
    })();
  }
  return oxaValidationSchemaPromise;
}

async function validateOxaDocument(record: OxaRecord): Promise<unknown> {
  const core = await getPanproto();
  const schema = await getOxaValidationSchema();
  return core.convert(record as object, { from: schema, to: schema });
}

function cloneComplement(document: Pick<MystDocument, "metadata" | "data"> | Pick<OxaDocument, "metadata" | "data">): DocumentComplement {
  return {
    metadata: { ...document.metadata },
    data: { ...document.data },
  };
}

function mergeDocumentFields<T extends { metadata?: Record<string, unknown>; data?: Record<string, unknown> }>(
  view: T,
  complement: DocumentComplement,
): { metadata: Record<string, unknown>; data: Record<string, unknown> } {
  return {
    metadata: { ...complement.metadata, ...(view.metadata ?? {}) },
    data: { ...complement.data, ...(view.data ?? {}) },
  };
}

function normalizeDocument<T extends { type: string; tree_json: string; metadata?: Record<string, unknown>; data?: Record<string, unknown> }>(
  document: T,
  schema: DocumentSchema,
  complement: DocumentComplement,
): MystDocument | OxaDocument {
  const { metadata, data } = mergeDocumentFields(document, complement);
  if (schema.format === "myst") {
    return {
      type: "document",
      tree_json: document.tree_json,
      metadata,
      data,
    };
  }
  return {
    type: "Document",
    tree_json: document.tree_json,
    metadata,
    data,
  };
}

async function convertDocument<Source, Target>(source: Source, from: BuiltSchema, to: BuiltSchema): Promise<Target> {
  const core = await getPanproto();
  return (await core.convert(source as object, { from, to })) as Target;
}

class CoreDocumentLens<Source extends MystDocument | OxaDocument, View extends DocumentView | ReverseDocumentView> {
  constructor(
    private readonly sourceSchema: DocumentSchema,
    private readonly targetSchema: DocumentSchema,
  ) {}

  async get(source: Source): Promise<LensResult<View, DocumentComplement>> {
    const { myst, oxa } = await getCoreDocumentSchemas();
    const from = this.sourceSchema.format === "myst" ? myst : oxa;
    const to = this.targetSchema.format === "myst" ? myst : oxa;
    const view = await convertDocument<Source, View>(source, from, to);
    return {
      view: {
        ...(view as View),
        type: this.targetSchema.format === "myst" ? "document" : "Document",
      },
      complement: cloneComplement(source),
    };
  }

  async put(view: View, complement: DocumentComplement): Promise<Source> {
    const { myst, oxa } = await getCoreDocumentSchemas();
    const from = this.targetSchema.format === "myst" ? myst : oxa;
    const to = this.sourceSchema.format === "myst" ? myst : oxa;
    const source = await convertDocument<View, Source>(view, from, to);
    return normalizeDocument(
      {
        ...(source as Source),
        type: this.sourceSchema.format === "myst" ? "document" : "Document",
      },
      this.sourceSchema,
      complement,
    ) as Source;
  }
}

export async function lens(sourceSchema: typeof mystDocumentSchema, targetSchema: typeof oxaDocumentSchema): Promise<Lens<MystDocument, DocumentView, DocumentComplement>>;
export async function lens(sourceSchema: typeof oxaDocumentSchema, targetSchema: typeof mystDocumentSchema): Promise<Lens<OxaDocument, ReverseDocumentView, DocumentComplement>>;
export async function lens(sourceSchema: unknown, targetSchema: unknown): Promise<Lens<MystDocument, DocumentView, DocumentComplement> | Lens<OxaDocument, ReverseDocumentView, DocumentComplement>> {
  if (!isDocumentSchema(sourceSchema) || !isDocumentSchema(targetSchema)) {
    throw new Error("Unsupported schema pair for panproto.lens");
  }

  if (sourceSchema.format === "myst" && targetSchema.format === "oxa") {
    return new CoreDocumentLens<MystDocument, DocumentView>(sourceSchema, targetSchema) as Lens<MystDocument, DocumentView, DocumentComplement>;
  }

  if (sourceSchema.format === "oxa" && targetSchema.format === "myst") {
    return new CoreDocumentLens<OxaDocument, ReverseDocumentView>(sourceSchema, targetSchema) as Lens<OxaDocument, ReverseDocumentView, DocumentComplement>;
  }

  throw new Error("Unsupported schema direction for panproto.lens");
}

export const panproto = {
  lens,
};
